import { Form } from "formik";
import styled, { css } from "styled-components";

import colors from "@src/common/styles/colors";
import {
  DEFAULT_MARGIN_L,
  DEFAULT_MARGIN_M,
  DEFAULT_MARGIN_XL,
} from "@src/common/styles/const";
import { defaultLetterSpacing, media } from "@src/common/styles/mixins";
import { SmallTileWrapper } from "@src/modules/DeviceStatus/shared/components/SmallTile/SmallTile.components";
import { StyledButton } from "@src/modules/shared/components/Button/Button.components";

interface GridProps {
  columnWidth: number;
  rowHeight: number;
  gap: number;
}

export const ViewWithSidebar = styled.div`
  display: flex;
  min-height: 100%;
  position: relative;

  ${media.up("LAPTOP")`
    margin-left: ${DEFAULT_MARGIN_M}px;
  `}

  ${media.down("MOBILE")`
    flex-direction: column;
  `}
`;

export const ViewWithSidebarContent = styled.div`
  width: 100%;
  background-color: ${colors.background};
  padding: ${DEFAULT_MARGIN_XL}px;

  ${media.down("MOBILE")`
    padding: 0;
  `}

  ${SmallTileWrapper} {
    align-self: center;
    margin-bottom: ${DEFAULT_MARGIN_M}px;
    width: 124px;
    height: 168px;
  }
`;

export const SidebarWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  min-width: 275px;
  background-color: ${colors.gray1};

  & > ${StyledButton} {
    ${media.up("TABLET")`
      margin: ${DEFAULT_MARGIN_M}px 0 40px ${DEFAULT_MARGIN_M}px;
    `}

    ${media.down("MOBILE")`
      margin-bottom: ${DEFAULT_MARGIN_M}px;
    `}
  }

  ${SmallTileWrapper} {
    align-self: center;
    width: 124px;
    height: 168px;
    margin-bottom: 48px;

    ${media.up("LAPTOP")`
        width: 195px;
        height: 234px;
        margin-bottom: 40px;
    `}

    ${media.down("MOBILE")`
        margin-bottom: 0;
    `}
  }

  ${media.only("TABLET")`
    min-width: 171px;
  `}

  ${media.down("MOBILE")`
    padding: ${DEFAULT_MARGIN_M}px;
    min-width: 100%;
    gap: ${DEFAULT_MARGIN_M}px;
    background-color: ${colors.background};
  `}
`;

export const TilesGrid = styled.div.attrs({
  "data-cy": "tiles-grid",
})<GridProps>`
  display: grid;
  grid-template-columns: repeat(
    auto-fill,
    minmax(${(p) => p.columnWidth}px, 1fr)
  );
  grid-auto-rows: minmax(${(p) => p.rowHeight}px, auto);
  grid-gap: ${(p) => p.gap}px;
`;

export const TilesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${DEFAULT_MARGIN_L}px;
  background-color: ${colors.white};
  padding: ${DEFAULT_MARGIN_L}px;
  height: 100%;

  ${media.down("MOBILE")`
    padding: ${DEFAULT_MARGIN_M}px;
  `}
`;

export const TilesHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  font-size: 18px;
  font-weight: 600;

  ${media.down("MOBILE")`
    font-size:14px;
  `}
`;

export const TilesName = styled.h2`
  flex-shrink: 0;
  margin: 0;
  font-size: inherit;
`;

export const AddFormWrapper = styled.div`
  ${media.up("TABLET")`
    padding: ${DEFAULT_MARGIN_M}px ;
  `}
`;

export const DeviceStatusFormWrapper = styled(Form)<{
  $hasRegularStyleOnMobile?: boolean;
}>`
  display: flex;
  flex-direction: column;
  border-radius: 32px;
  padding: ${DEFAULT_MARGIN_L}px;
  background-color: ${colors.white};

  ${media.down("MOBILE")`
    background-color: transparent;
    margin: 0;
    padding: ${DEFAULT_MARGIN_M}px;

    & > ${StyledButton}{
      align-self: flex-start;
      margin-bottom: ${DEFAULT_MARGIN_M}px;
    }

    & > ${SmallTileWrapper}{
      margin-top: ${DEFAULT_MARGIN_M}px;
      margin-bottom: ${DEFAULT_MARGIN_XL}px;  
    }
  `}

  ${(p) =>
    p.$hasRegularStyleOnMobile &&
    css`
      ${media.down("MOBILE")`
        margin: ${DEFAULT_MARGIN_M}px;
        background-color: ${colors.white};
      `}
    `}
`;

export const DeviceStatusHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 28px;
  color: ${colors.almostBlack};

  ${media.down("MOBILE")`
    padding: ${DEFAULT_MARGIN_M}px 0;
    font-weight: 600;
    font-size: 14px;
  `}
`;

export const ButtonsWrapper = styled.div`
  display: flex;

  & > :first-child {
    margin-right: ${DEFAULT_MARGIN_M}px;
  }
`;

export const BackButtonWrapper = styled.div`
  display: flex;
  align-items: center;
`;

export const BottomButtonWrapper = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  background-color: ${colors.background};
  padding: ${DEFAULT_MARGIN_M}px;
  width: 100%;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.15);

  ${StyledButton} {
    width: 100%;
  }

  ${media.down("MOBILE")`
    z-index: 1;
  `}
`;

export const UppercaseLabel = styled.label`
  text-transform: uppercase;
  color: ${colors.gray2};
  ${defaultLetterSpacing};
  font-size: 12px;
  font-weight: 400;
`;

export const CenterButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 10px;
`;

export const TextBoxCenter = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 90%;
  margin: 10px;
`;

export const IconButton = styled.div`
  display: block;
  margin: 10px;
`;
